import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject } from '@angular/core';
import { L10n, L10nBase } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';

import { ButtonComponent, DialogButtonResult, DialogContentComponentBase, DialogContentComponentIns, DialogContentViewModel, DialogHeaderComponentIns, DialogInsService, DialogState } from '@skylab-angular-shared/ui';
import { head } from '@skylab-shared/utils';

export enum KsefDocumentInvalidXmlActionOptions {
	CorrectDocument,
	SaveDocumentWithInvalidXml
}

@Component({
	selector: 'skl-ksef-document-invalid-xml-dialog',
	template: `
		<skl-dialog-content-ins>
			<skl-dialog-header-ins
				id="ksef-document-invalid-xml-failed-dialog-header"
				[title]="'scenes.shared.dialog.ksef-document-invalid-xml.messages.header' | translate"
				(onClose)="onCancel()">
			</skl-dialog-header-ins>
			<ins-content>
				<div class="main">
					<div class="description-box text-description" id="ksef-document-invalid-xml-failed-dialog-description">
						<div>{{'scenes.shared.dialog.ksef-document-invalid-xml.messages.description' | translate}}</div>
						<div>{{'scenes.shared.dialog.ksef-document-invalid-xml.messages.possible-actions' | translate}}</div>
					</div>
					<div class="options-wrapper" id="ksef-document-invalid-xml-failed-dialog-options">
						@for (option of options; track option) {
							<div class="select-option" (click)="selectOption(option.value)">
								@if (isSelected(option.value)) {
									<div class="check-icon">
										<ins-icon name="check" size="26px" shape="circle" inner-color="white" color="primary"></ins-icon>
									</div>
								}
								<div class="check-icon">
									@if (!isSelected(option.value)) {
										<div class="unselected-box"></div>
									}
								</div>
								<div class="description">
									<div class="align--tiny-bottom">{{option.label}}</div>
									<div class="text-description text-small">{{option.description}}</div>
								</div>
							</div>
						}
					</div>
				</div>
			</ins-content>

			<ins-footer class="dialog__buttons">
				<skl-button
					id="ksef-document-invalid-xml-failed-dialog-confirm-button"
					typeName="accent"
					(click)="onConfirm()">
					{{'scenes.shared.dialog.ksef-document-invalid-xml.buttons.confirm' | translate}}
				</skl-button>
			</ins-footer>
		</skl-dialog-content-ins>
	`,
	styleUrl: `../create-inventory-document-to-commercial-document-failed/create-inventory-document-to-commercial-document-failed-dialog.component.scss`,
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [DialogContentComponentIns, DialogHeaderComponentIns, ButtonComponent, TranslatePipe]
})
export class KsefDocumentInvalidXmlActionDialog extends DialogContentComponentBase<DialogContentViewModel> {
	private selected!: KsefDocumentInvalidXmlActionOptions;;
	public options: { label: string, description: string, value: KsefDocumentInvalidXmlActionOptions }[] = [];

	public constructor(
		dialogService: DialogInsService,
		@Inject(L10n) private readonly l10n: L10nBase
	) {
		super(dialogService);
	}

	protected override async prepareDataAsync(): Promise<void> {
		this.options = [
			{
				label: this.l10n._T(`scenes.shared.dialog.ksef-document-invalid-xml.options.correct-document.label`),
				description: this.l10n._T(`scenes.shared.dialog.ksef-document-invalid-xml.options.correct-document.description`),
				value: KsefDocumentInvalidXmlActionOptions.CorrectDocument
			},
			{
				label: this.l10n._T(`scenes.shared.dialog.ksef-document-invalid-xml.options.save.label`),
				description: this.l10n._T(`scenes.shared.dialog.ksef-document-invalid-xml.options.save.description`),
				value: KsefDocumentInvalidXmlActionOptions.SaveDocumentWithInvalidXml
			}
		];

		this.selected = head(this.options).value;
	}

	public selectOption(option: KsefDocumentInvalidXmlActionOptions): void {
		this.selected = option;
	}

	public isSelected(option: KsefDocumentInvalidXmlActionOptions): boolean {
		return option === this.selected;
	}

	public override onCancel(): void {
		this.onAction({state: DialogState.Cancel});
	}

	public onConfirm(): void {
		this.onAction({state: DialogState.Confirm});
	}

	public override onAction(result: DialogButtonResult): void {
		const value = result.state === DialogState.Cancel ? KsefDocumentInvalidXmlActionOptions.CorrectDocument : this.selected;
		this.dialogService.close({state: DialogState.Confirm, value});
	}
}
